import { format, parseISO } from "date-fns";
import type { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import type { Vehicle } from "@rumble-it/common-lib/types/models/Vehicle.interface";
import { fetchDossiers } from "@/lib/api/resources/dossiers";

export interface DashboardTableRow {
  _id: string;
  _href: string;
  [key: string]: unknown;
}

export interface DashboardTableData {
  rows: DashboardTableRow[];
  total: number;
}

export type DataSourceAdapter = () => Promise<DashboardTableData>;

function formatVehicle(vehicle: Vehicle | null | undefined): string {
  if (vehicle == null) {
    return "-";
  }

  const makeAndModel = [vehicle.brand, vehicle.model]
    .filter((part): part is string => part != null && part !== "")
    .join(" ");

  if (makeAndModel === "") {
    return "-";
  }

  return vehicle.buildYear != null
    ? `${makeAndModel} (${vehicle.buildYear})`
    : makeAndModel;
}

function mapDossierToRow(dossier: Dossier): DashboardTableRow {
  return {
    _id: dossier.id,
    _href: `/dossiers/${dossier.id}`,
    licensePlate: dossier.vehicle?.licensePlate?.identifier ?? "-",
    vehicle: formatVehicle(dossier.vehicle),
    createdAt: format(parseISO(dossier.createdAt), "dd-MM-yyyy HH:mm"),
  };
}

// The /dossiers collection uses the API's default page size and does not honour
// a client-supplied itemsPerPage, so we cap the number of rows client-side. The
// overflow count still reflects the true total via totalItems.
const MAX_UNREAD_ROWS = 10;

const recentUnreadDossiers: DataSourceAdapter = async () => {
  const response = await fetchDossiers({
    "exists[adminFirstReadAt]": false,
    "order[createdAt]": "desc",
    itemsPerPage: MAX_UNREAD_ROWS,
  });

  return {
    rows: response.member.slice(0, MAX_UNREAD_ROWS).map(mapDossierToRow),
    total: response.totalItems,
  };
};

export const RECENT_UNREAD_DOSSIERS = "recentUnreadDossiers";

const adapters: Record<string, DataSourceAdapter> = {
  [RECENT_UNREAD_DOSSIERS]: recentUnreadDossiers,
};

export const getDataSourceAdapter = (
  dataSource: string,
): DataSourceAdapter | null => adapters[dataSource] ?? null;
